/**
 * Settings ▸ Taxonomy — business dimensions (line of business, department,
 * business application, custom) and their controlled values.
 *
 * Promoted out of the GitHub page (workspace-organization spec §3); repos are
 * tagged with these values where they are listed, on Settings ▸ GitHub.
 */

import { useCallback, useEffect, useState } from 'react';
import { Tags } from 'lucide-react';
import { TaxonomyManager } from '@/components/settings/TaxonomyManager';
import { projectsApi, type TaxonomyDimension } from '@/services/projectsApi';
import { useTenant } from '@/contexts/TenantContext';

export default function TaxonomyPage() {
  const { tenant } = useTenant();
  const isAdmin = ['admin', 'owner'].includes(tenant?.role ?? '');

  const [dimensions, setDimensions] = useState<TaxonomyDimension[]>([]);

  const loadTaxonomy = useCallback(async () => {
    try {
      setDimensions(await projectsApi.getTaxonomy());
    } catch {
      // Taxonomy is optional chrome — the page renders without it.
    }
  }, []);

  useEffect(() => {
    loadTaxonomy();
  }, [loadTaxonomy]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Tags className="w-5 h-5 text-slate-500" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Taxonomy</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Business dimensions used to tag repositories — never access-relevant.
          </p>
        </div>
      </div>

      {isAdmin ? (
        <TaxonomyManager dimensions={dimensions} onChanged={loadTaxonomy} />
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          The business taxonomy is managed by workspace admins.
        </p>
      )}
    </div>
  );
}
